import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, backupAPI } from '../lib/api';
import { ProductionWeekCard } from '../components/production/ProductionWeekCard';
import { SalesTrendChart } from '../components/dashboard/SalesTrendChart';
import { LeaderboardTable } from '../components/dashboard/LeaderboardTable';
import { SalesBySellerChart } from '../components/dashboard/SalesBySellerChart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Users,
    Euro,
    CheckCircle2,
    XCircle,
    Truck,
    CalendarRange,
    CalendarDays,
    TrendingUp,
    TrendingDown,
    Phone,
    Mail,
    Calendar as CalendarIcon,
    ClipboardList,
    Loader2,
    DatabaseBackup,
    CheckCircle,
    AlertTriangle
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList
} from 'recharts';

const formatCurrency = (value) =>
    new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value || 0);

const formatK = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`);

const pctChange = (cur, prev) => {
    if (!prev) return (cur || 0) > 0 ? 100 : null;
    return ((cur - prev) / prev) * 100;
};

const TrendBadge = ({ cur, prev }) => {
    const p = pctChange(cur, prev);
    if (p === null) return null;
    const up = p >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
            <Icon size={13} /> {up ? '+' : ''}{p.toFixed(0)}%
            <span className="text-muted-foreground font-normal">vs periodo prec.</span>
        </span>
    );
};

const DashboardPage = () => {
    const { user, isAdmin, canViewTeam } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backupStatus, setBackupStatus] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const statsRes = await dashboardAPI.getStats();
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
        if (isAdmin()) {
            try {
                const bk = await backupAPI.getSettings();
                setBackupStatus(bk.data);
            } catch { /* noop */ }
        }
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'call': return <Phone size={16} className="text-blue-500" />;
            case 'meeting': return <CalendarIcon size={16} className="text-purple-500" />;
            case 'email': return <Mail size={16} className="text-green-500" />;
            case 'task': return <ClipboardList size={16} className="text-orange-500" />;
            case 'delivery': return <Truck size={16} className="text-red-500" />;
            case 'confirmation': return <CheckCircle2 size={16} className="text-blue-600" />;
            default: return <ClipboardList size={16} />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const monthlySales = (stats?.monthly_sales || []).map((m) => ({ ...m }));
    const monthlyDeliveries = (stats?.monthly_deliveries || []).map((m) => ({ ...m }));
    const weeklySales = (stats?.weekly_sales || []).map((w) => ({ ...w }));

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-3xl uppercase tracking-tight">Dashboard</h1>
                <p className="text-muted-foreground">
                    Benvenuto, {user?.name}
                </p>
            </div>

            {/* Backup status banner (admin only) */}
            {isAdmin() && backupStatus?.last_auto && (
                (() => {
                    const la = backupStatus.last_auto;
                    const when = la.at ? new Date(la.at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    const ok = la.status === 'success';
                    return (
                        <Link
                            to="/backup"
                            data-testid="dashboard-backup-banner"
                            className={`flex items-center gap-3 p-4 rounded-sm border text-sm transition-colors ${ok ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' : 'bg-destructive/10 border-destructive/30 hover:bg-destructive/20'}`}
                        >
                            {ok ? <CheckCircle size={20} className="text-emerald-600 shrink-0" /> : <AlertTriangle size={20} className="text-destructive shrink-0" />}
                            <div className="flex-1 min-w-0">
                                {ok ? (
                                    <p className="text-emerald-800">
                                        <b>Ultimo backup automatico completato</b> il {when}
                                        {la.docs_count ? ` — ${la.docs_count} documenti` : ''}.
                                    </p>
                                ) : (
                                    <p className="text-destructive">
                                        <b>Backup automatico fallito</b> il {when}. Apri la pagina Backup per creare una copia manuale.
                                    </p>
                                )}
                            </div>
                            <DatabaseBackup size={18} className={`shrink-0 ${ok ? 'text-emerald-600/50' : 'text-destructive/50'}`} />
                        </Link>
                    );
                })()
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="stats-card stats-card-info" data-testid="stat-week-sales">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                    Vendite Settimana
                                </p>
                                <p className="text-3xl font-heading font-bold mt-1">
                                    {formatCurrency(stats?.sales_week_value)}
                                </p>
                            </div>
                            <CalendarDays className="h-10 w-10 text-blue-500/20" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">
                            {stats?.sales_week_count || 0} offerte confermate
                        </p>
                        <div className="mt-1" data-testid="week-trend">
                            <TrendBadge cur={stats?.sales_week_value} prev={stats?.sales_week_prev_value} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="stats-card stats-card-primary" data-testid="stat-month-sales">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                    Vendite Mese
                                </p>
                                <p className="text-3xl font-heading font-bold mt-1">
                                    {formatCurrency(stats?.sales_month_value)}
                                </p>
                            </div>
                            <CalendarRange className="h-10 w-10 text-primary/20" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">
                            {stats?.sales_month_count || 0} offerte confermate
                        </p>
                        <div className="mt-1" data-testid="month-trend">
                            <TrendBadge cur={stats?.sales_month_value} prev={stats?.sales_month_prev_value} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="stats-card stats-card-success" data-testid="stat-confirmed">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                    Offerte Confermate
                                </p>
                                <p className="text-3xl font-heading font-bold mt-1">
                                    {stats?.confirmed_offers || 0}
                                </p>
                            </div>
                            <CheckCircle2 className="h-10 w-10 text-green-500/20" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">
                            {formatCurrency(stats?.confirmed_value)} totali
                        </p>
                    </CardContent>
                </Card>

                <Card className="stats-card stats-card-warning" data-testid="stat-deliveries">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                    Consegne Mese
                                </p>
                                <p className="text-3xl font-heading font-bold mt-1">
                                    {stats?.deliveries_month_count || 0}
                                </p>
                            </div>
                            <Truck className="h-10 w-10 text-yellow-600/20" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">
                            {formatCurrency(stats?.deliveries_month_value)} da consegnare
                        </p>
                        <div className="mt-1" data-testid="deliveries-trend">
                            <TrendBadge cur={stats?.deliveries_month_value} prev={stats?.deliveries_month_prev_value} />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Andamento + clienti */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <SalesTrendChart monthlySales={monthlySales} monthlyDeliveries={monthlyDeliveries} />
                <Card data-testid="stat-clients">
                    <CardHeader>
                        <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                            <Users size={18} /> Clienti
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-4xl font-heading font-bold">{stats?.total_clients || 0}</p>
                        <div className="flex items-center gap-4 mt-4 text-sm">
                            <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle2 size={14} />
                                {stats?.active_clients || 0} attivi
                            </span>
                            <span className="flex items-center gap-1 text-red-600">
                                <XCircle size={14} />
                                {stats?.lost_clients || 0} persi
                            </span>
                        </div>
                        <div className="mt-6 pt-4 border-t text-sm text-muted-foreground">
                            Offerte perse: <span className="font-medium text-foreground">{stats?.lost_offers || 0}</span>
                        </div>
                    </CardContent>
                </Card>

            </div>

            <LeaderboardTable />

            <div className={`grid grid-cols-1 gap-6 ${(isAdmin() || canViewTeam() || user?.role === 'backoffice') ? 'lg:grid-cols-2' : ''}`}>
                {(isAdmin() || canViewTeam() || user?.role === 'backoffice') && <SalesBySellerChart />}
                {/* Vendite Settimanali (importo) */}
                <Card data-testid="weekly-sales-chart">
                    <CardHeader>
                        <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                            <CalendarDays size={18} /> Vendite Settimanali — ultime 8 settimane (importo)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={weeklySales} margin={{ top: 16, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="label" fontSize={12} />
                                <YAxis tickFormatter={formatK} fontSize={12} />
                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                <Bar dataKey="value" name="Venduto" fill="#1976D2" radius={[4, 4, 0, 0]}>
                                    <LabelList dataKey="value" position="top" formatter={(v) => (v ? formatK(v) : '')} fontSize={11} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            <ProductionWeekCard />

            {/* Recent Activities */}
            <Card data-testid="recent-activities">
                <CardHeader>
                    <CardTitle className="font-heading uppercase text-lg">
                        Attività Recenti
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {stats?.recent_activities?.length > 0 ? (
                        <div className="space-y-3">
                            {stats.recent_activities.map((activity) => (
                                <div
                                    key={activity.id}
                                    className="flex items-center gap-4 p-3 rounded-sm bg-muted/30 hover:bg-muted/50 transition-colors"
                                >
                                    {getActivityIcon(activity.type)}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{activity.title}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(activity.scheduled_at).toLocaleDateString('it-IT', {
                                                day: 'numeric',
                                                month: 'short',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    {activity.completed ? (
                                        <Badge className="bg-green-100 text-green-700">Completata</Badge>
                                    ) : (
                                        <Badge variant="outline">Da fare</Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-center py-8">
                            Nessuna attività recente
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DashboardPage;
